import { GEMINI_ACTIVE_MODEL_ID } from "@automatry/schemas";
import { packIoComRelayModules } from "../ioComRelaySizing.js";
export const AGENT_TOOL_CONTRACT_VERSION = "agent-tool-result.v1";
export const AGENT_TOOL_IDS = {
    zoneRoomCoverage: "agent_tool.zone_room_coverage.check",
    controllerCapacity: "agent_tool.controller_capacity.calculate",
    spareSecondZone: "agent_tool.spare_second_zone.calculate",
    overflowGuideline: "agent_tool.overflow_guideline.check",
    infrastructureCapacity: "agent_tool.infrastructure_capacity.check",
    meteringProtocolFit: "agent_tool.metering_protocol_fit.check",
    catalogProtocolGrounding: "agent_tool.catalog_protocol_grounding.check",
    policyDefaultLabelling: "agent_tool.policy_default_labelling.check",
    provenanceCompleteness: "agent_tool.provenance_completeness.check",
    controllerAllocationAgreement: "agent_tool.controller_allocation_agreement.check"
};
export const AGENT_TOOL_CONVERGENCE_DEFAULTS = {
    maxApartmentRepairRetries: 3,
    maxGlobalAggregatorRounds: 2,
    model: GEMINI_ACTIVE_MODEL_ID,
    modelEscalation: "none"
};
const IO_COM_RELAY_SIZING_RULE_TRACE_REFS = [
    "rule-infrastructure-load-relay-io-com-modules",
    "trace-load-relay-io-com",
    "agent-io-com-relay-sizing-evidence"
];
const SRM_SP_RELAY_CANONICAL_PART_NO = "SRM/SP/24VAC";
const SRM_SP_RELAY_ACCEPTED_PART_NOS = new Set([
    SRM_SP_RELAY_CANONICAL_PART_NO,
    "SRM/SP/24AC"
]);
const SRM_RELAY_SOURCE_TRACE_REFS_BY_SOURCE = {
    towel_rail: [
        "rule-load-towel-rail-central-relay",
        "trace-towel-rail-central-load-relays"
    ],
    eufh: ["trace-eufh-load-points"],
    source_enable: ["trace-source-enable-load-relays", "trace-source-enable-output"],
    remote_high_load: ["trace-remote-high-load-relays", "rule-remote-high-load-relay"]
};
const SRM_RELAY_SOURCE_SPECIFIC_TRACE_REFS = new Set(Object.values(SRM_RELAY_SOURCE_TRACE_REFS_BY_SOURCE)
    .flat()
    .map((ref) => normalizeEvidenceRef(ref)));
const EVIDENCE_BACKED_MATERIAL_BASIS = new Set([
    "tender_evidence",
    "catalog_scope_evidence"
]);
const GENERIC_MATERIAL_SUBJECT_TOKENS = new Set([
    "infra",
    "item",
    "entry",
    "material",
    "proposal",
    "scope",
    "basis",
    "install",
    "installed",
    "included",
    "true",
    "false",
    "other",
    "others",
    "titan",
    "source",
    "policy",
    "default",
    "evidence",
    "resolved",
    "unresolved"
]);
const POINT_KINDS = ["ui", "di", "ao", "do", "relay"];
const ZERO_POINTS = { ui: 0, di: 0, ao: 0, do: 0, relay: 0 };
const DEFAULT_SECONDARY_ZONE_DEMAND = {
    ui: 1,
    di: 0,
    ao: 0,
    do: 1,
    relay: 0
};
export function checkZoneAndRoomCoverage(input) {
    const knownZoneIds = new Set(input.zones.map((zone) => zone.id));
    const zoneCoverage = new Map();
    for (const coverage of input.proposalCoverage) {
        zoneCoverage.set(coverage.zoneId, [
            ...(zoneCoverage.get(coverage.zoneId) ?? []),
            coverage
        ]);
    }
    const uncoveredZones = input.zones
        .filter((zone) => (zoneCoverage.get(zone.id) ?? []).length === 0)
        .map((zone) => zone.id)
        .sort();
    const doubleCoveredZones = input.zones
        .filter((zone) => (zoneCoverage.get(zone.id) ?? []).length > 1)
        .map((zone) => zone.id)
        .sort();
    const unknownZoneIds = [
        ...new Set(input.proposalCoverage
            .map((coverage) => coverage.zoneId)
            .filter((zoneId) => !knownZoneIds.has(zoneId)))
    ].sort();
    const roomFacts = summarizeRoomCoverage(input, zoneCoverage);
    const facts = [
        fact({
            kind: "coverage.uncovered_zones",
            label: "Uncovered take-off zones",
            value: uncoveredZones,
            details: { uncoveredZoneCount: uncoveredZones.length }
        }),
        fact({
            kind: "coverage.double_covered_zones",
            label: "Double-covered take-off zones",
            value: doubleCoveredZones,
            details: { doubleCoveredZoneCount: doubleCoveredZones.length }
        }),
        fact({
            kind: "coverage.unknown_zone_ids",
            label: "Proposal coverage rows referencing unknown take-off zones",
            value: unknownZoneIds,
            details: { unknownZoneIdCount: unknownZoneIds.length }
        }),
        ...roomFacts
    ];
    const violations = [];
    if (uncoveredZones.length > 0) {
        violations.push(violation({
            id: `${input.apartmentTypeId}-uncovered-zones`,
            code: "coverage.zone_uncovered",
            severity: "blocker",
            why: `${uncoveredZones.length} take-off zone(s) have no proposed coverage.`,
            suggestedRecheckPromptFragment: "Recheck the take-off against the controller proposal and cover every uncovered zone before returning.",
            details: { uncoveredZones }
        }));
    }
    if (doubleCoveredZones.length > 0) {
        violations.push(violation({
            id: `${input.apartmentTypeId}-double-covered-zones`,
            code: "coverage.zone_double_covered",
            severity: "warning",
            why: `${doubleCoveredZones.length} take-off zone(s) are covered by more than one proposal entry.`,
            suggestedRecheckPromptFragment: "Recheck double-covered zones and decide whether each is a real combined load or duplicate allocation.",
            details: { doubleCoveredZones }
        }));
    }
    if (unknownZoneIds.length > 0) {
        violations.push(violation({
            id: `${input.apartmentTypeId}-unknown-zone-coverage`,
            code: "coverage.unknown_zone_referenced",
            severity: "blocker",
            why: `${unknownZoneIds.length} proposal coverage row zone id(s) are not present in the take-off zone set.`,
            suggestedRecheckPromptFragment: "Recheck proposal coverage against the take-off zone ids; remove or remap every unknown zone id before returning.",
            details: { unknownZoneIds }
        }));
    }
    return result(AGENT_TOOL_IDS.zoneRoomCoverage, facts, violations, {
        expectedZones: input.zones.length,
        uncoveredZones: uncoveredZones.length,
        doubleCoveredZones: doubleCoveredZones.length,
        unknownZoneIds: unknownZoneIds.length
    });
}
export function calculateControllerCapacity(input) {
    const controllerSummaries = input.controllers.map((controller) => summarizeControllerCapacity(controller, input.pointDemands ?? []));
    const facts = controllerSummaries.map((summary) => fact({
        kind: "controller.capacity",
        label: `${summary.controllerId} point and zone usage`,
        subjectRef: summary.controllerId,
        value: {
            pointsUsed: summary.pointsUsed,
            pointCapacity: summary.pointCapacity,
            pointSpare: summary.pointSpare,
            zonesAssigned: summary.zonesAssigned,
            zonesSupported: summary.zonesSupported
        },
        details: {
            productPartNo: summary.productPartNo,
            role: summary.role ?? "unknown"
        }
    }));
    const violations = controllerSummaries.flatMap((summary) => {
        const pointViolations = POINT_KINDS.flatMap((pointKind) => summary.pointsUsed[pointKind] > summary.pointCapacity[pointKind]
            ? [
                violation({
                    id: `${summary.controllerId}-${pointKind}-over-capacity`,
                    code: "controller.point_capacity_exceeded",
                    severity: "blocker",
                    subjectRef: summary.controllerId,
                    why: `${summary.controllerId} uses ${summary.pointsUsed[pointKind]} ${pointKind.toUpperCase()} point(s) but capacity is ${summary.pointCapacity[pointKind]}.`,
                    suggestedRecheckPromptFragment: "Split this controller's loads across legal thermal controllers or revise the proposed product capacity.",
                    details: {
                        pointKind,
                        pointsUsed: summary.pointsUsed[pointKind],
                        pointCapacity: summary.pointCapacity[pointKind]
                    }
                })
            ]
            : []);
        if (summary.zonesAssigned <= summary.zonesSupported) {
            return pointViolations;
        }
        return [
            ...pointViolations,
            violation({
                id: `${summary.controllerId}-zone-over-capacity`,
                code: "controller.zone_capacity_exceeded",
                severity: "blocker",
                subjectRef: summary.controllerId,
                why: `${summary.controllerId} has ${summary.zonesAssigned} zone slot(s) but supports ${summary.zonesSupported}.`,
                suggestedRecheckPromptFragment: "Recheck zone-slot packing for this controller; do not over-pack a controller to make the count work.",
                details: {
                    zonesAssigned: summary.zonesAssigned,
                    zonesSupported: summary.zonesSupported
                }
            })
        ];
    });
    return result(AGENT_TOOL_IDS.controllerCapacity, facts, violations, {
        controllersChecked: input.controllers.length,
        overCapacityControllers: new Set(violations
            .map((item) => item.subjectRef)
            .filter((subjectRef) => subjectRef !== undefined)).size
    });
}
export function calculateSpareSecondZones(input) {
    const controllerSummaries = input.controllers.map((controller) => summarizeControllerCapacity(controller, input.pointDemands ?? []));
    const secondaryZoneDemand = input.secondaryZoneDemand ?? DEFAULT_SECONDARY_ZONE_DEMAND;
    const spareSecondZoneSlotControllerIds = controllerSummaries
        .filter((summary) => hasSpareSecondZoneSlot(summary, secondaryZoneDemand))
        .map((summary) => summary.controllerId)
        .sort();
    const totalSpareOutputs = controllerSummaries.reduce((total, summary) => total + summary.pointSpare.do + summary.pointSpare.relay, 0);
    const proposedMzControllerCount = controllerSummaries.filter((summary) => summary.productPartNo === "MZ-503" || summary.role === "mz_overflow").length;
    const assignedZoneIds = collectAssignedZoneIds(input.controllers);
    const associationCandidateZones = input.zones?.filter(isSecondZoneAssociationCandidate);
    const unassociatedZoneIds = associationCandidateZones
        ?.filter((zone) => !assignedZoneIds.has(zone.id))
        .map((zone) => zone.id)
        .sort() ?? [];
    const associationContextProvided = input.zones !== undefined;
    const facts = [
        fact({
            kind: "spare.fcu_second_zone_slots_available",
            label: "FCU 710R controllers with a spare second-zone slot",
            value: spareSecondZoneSlotControllerIds,
            details: {
                spareSecondZoneSlotCount: spareSecondZoneSlotControllerIds.length,
                secondaryZoneDemand,
                semantics: "controller-local slot/capacity fact only; association validity needs the take-off zone set"
            }
        }),
        fact({
            kind: "spare.total_spare_outputs",
            label: "Whole-proposal spare DO plus relay outputs",
            value: totalSpareOutputs,
            details: {
                spareOutputsAreDoPlusRelay: true
            }
        }),
        fact({
            kind: associationContextProvided
                ? "spare.association_context_provided"
                : "spare.association_question_unanswerable",
            label: associationContextProvided
                ? "Take-off zone set supplied for second-zone association check"
                : "Second-zone association question is unanswerable without take-off zone set",
            value: associationContextProvided ? "answerable" : "unanswerable",
            details: {
                associationContextProvided,
                associationCandidateZoneCount: associationCandidateZones?.length ?? 0,
                unassociatedZoneCount: associationContextProvided
                    ? unassociatedZoneIds.length
                    : undefined
            }
        })
    ];
    if (associationContextProvided) {
        facts.push(fact({
            kind: "spare.unassociated_takeoff_zones",
            label: "Unassociated take-off zones remaining after proposed slots",
            value: unassociatedZoneIds,
            details: {
                unassociatedZoneCount: unassociatedZoneIds.length,
                spareSecondZoneSlotCount: spareSecondZoneSlotControllerIds.length
            }
        }));
    }
    const violations = associationContextProvided &&
        proposedMzControllerCount > 0 &&
        unassociatedZoneIds.length > 0 &&
        spareSecondZoneSlotControllerIds.length > 0
        ? [
            violation({
                id: `${input.apartmentTypeId}-mz-with-spare-second-zone-slots`,
                code: "spare.mz_with_spare_second_zone_slots_and_unassociated_zones",
                severity: "warning",
                why: `${unassociatedZoneIds.length} unassociated take-off zone(s) remain while ${spareSecondZoneSlotControllerIds.length} spare FCU second-zone slot(s) remain and ${proposedMzControllerCount} MZ controller(s) are proposed.`,
                suggestedRecheckPromptFragment: `you have ${unassociatedZoneIds.length} unassociated zone(s) and ${spareSecondZoneSlotControllerIds.length} spare FCU second-zone slot(s) - recheck association before adding overflow controls`,
                details: {
                    unassociatedZoneIds,
                    spareSecondZoneSlotControllerIds,
                    proposedMzControllerCount
                }
            })
        ]
        : [];
    return result(AGENT_TOOL_IDS.spareSecondZone, facts, violations, {
        spareSecondZoneSlots: spareSecondZoneSlotControllerIds.length,
        totalSpareOutputs,
        proposedMzControllerCount,
        associationContextProvided: associationContextProvided ? 1 : 0,
        associationUnanswerable: associationContextProvided ? 0 : 1,
        unassociatedZoneCount: associationContextProvided ? unassociatedZoneIds.length : 0
    });
}
export function checkOverflowGuideline(input) {
    const takeoff = normalizeOverflowTakeoff(input.takeoff);
    const heatingZoneCount = takeoff.wufhZoneCount +
        takeoff.eufhZoneCount +
        (takeoff.standaloneHeatOnlyZoneCount ?? 0);
    const fcuHeatingCoverage = takeoff.fcuCount * 2;
    const heatingZonesBeyondPrimaryFcuCount = Math.max(0, heatingZoneCount - takeoff.fcuCount);
    const availableFcuSecondZoneCapacity = takeoff.fcuCount;
    const genuineOverflowBeforeExtra710 = Math.max(0, heatingZoneCount - fcuHeatingCoverage);
    const fcuControllerCount = input.controllers.filter((controller) => controller.role === "fcu_primary").length;
    const heatOnly710Count = input.controllers.filter((controller) => controller.role === "heat_only_710").length;
    const mzControllerCount = input.controllers.filter((controller) => controller.role === "mz_overflow" || controller.productPartNo === "MZ-503").length;
    const heatOnly710Capacity = heatOnly710Count * 2;
    const genuineOverflowAfterHeatOnly710 = Math.max(0, genuineOverflowBeforeExtra710 - heatOnly710Capacity);
    const mzZoneCapacity = mzControllerCount * 4;
    const mzCapacityExcessAfterHeatOnly710 = Math.max(0, mzZoneCapacity - genuineOverflowAfterHeatOnly710);
    const proposalStillHasEnoughMzCapacityAfterRemovingOne = mzControllerCount > 0 &&
        Math.max(0, mzControllerCount - 1) * 4 >= genuineOverflowAfterHeatOnly710;
    const totalControlsCount = fcuControllerCount + heatOnly710Count + mzControllerCount;
    const facts = [
        fact({
            kind: "overflow.total_controls_count",
            label: "Total proposed thermal controls",
            value: totalControlsCount,
            details: {
                fcuControllerCount,
                heatOnly710Count,
                mzControllerCount,
                minimizeControlsIntent: true
            }
        }),
        fact({
            kind: "overflow.fcu_heating_zone_arithmetic",
            label: "FCU approximate UFH-zone coverage arithmetic",
            value: `${takeoff.fcuCount} FCU ~= ${fcuHeatingCoverage} UFH-zone coverage`,
            details: {
                fcuCount: takeoff.fcuCount,
                heatingZoneCount,
                fcuHeatingCoverage
            }
        }),
        fact({
            kind: "overflow.fcu_second_zone_capacity_arithmetic",
            label: "Heating zones beyond primary FCU count versus approximate FCU second-zone capacity",
            value: {
                heatingZonesBeyondPrimaryFcuCount,
                availableFcuSecondZoneCapacity
            },
            details: {
                heatingZoneCount,
                fcuCount: takeoff.fcuCount
            }
        }),
        fact({
            kind: "overflow.genuine_overflow_zone_count",
            label: "Genuine overflow zone count after approximate FCU second-zone coverage",
            value: genuineOverflowBeforeExtra710,
            details: {
                genuineOverflowBeforeExtra710,
                heatOnly710Capacity,
                genuineOverflowAfterHeatOnly710
            }
        }),
        fact({
            kind: "overflow.proposed_mz_total_capacity",
            label: "Proposed MZ total UFH-zone capacity",
            value: mzZoneCapacity,
            details: {
                mzControllerCount,
                mzZonesPerController: 4
            }
        }),
        fact({
            kind: "overflow.proposed_mz_capacity_excess_zones",
            label: "Proposed MZ capacity exceeding genuine overflow",
            value: mzCapacityExcessAfterHeatOnly710,
            details: {
                proposedMzTotalCapacity: mzZoneCapacity,
                genuineOverflowAfterHeatOnly710
            }
        })
    ];
    if (genuineOverflowBeforeExtra710 > 0 && genuineOverflowBeforeExtra710 <= 2) {
        facts.push(fact({
            kind: "overflow.small_residual_boundary",
            label: "Small residual UFH-zone boundary",
            value: genuineOverflowBeforeExtra710,
            details: {
                recognizedHeatOnly710Count: heatOnly710Count
            }
        }));
    }
    if (heatOnly710Count > 0) {
        facts.push(fact({
            kind: "overflow.heat_only_710_role_recognized",
            label: "heat_only_710 role recognized as 710R panel capacity",
            value: heatOnly710Count,
            details: {
                heatOnly710Capacity,
                genuineOverflowAfterHeatOnly710
            }
        }));
    }
    const violations = [];
    if (mzControllerCount > 0 && proposalStillHasEnoughMzCapacityAfterRemovingOne) {
        violations.push(violation({
            id: `${takeoff.apartmentTypeId}-over-overflow-mz`,
            code: "overflow.over_overflow_mz_capacity",
            severity: "warning",
            why: `${mzControllerCount} MZ controller(s) provide ${mzZoneCapacity} overflow zones, but only ${genuineOverflowAfterHeatOnly710} genuine overflow zone(s) remain after FCU and heat_only_710 coverage.`,
            suggestedRecheckPromptFragment: "your MZ capacity exceeds the genuine overflow left after FCU second-zone association - recheck whether fewer/smaller controls or spare-zone association fits the guideline",
            details: {
                mzControllerCount,
                proposedMzTotalCapacity: mzZoneCapacity,
                genuineOverflowAfterHeatOnly710,
                proposedMzCapacityExcessZones: mzCapacityExcessAfterHeatOnly710
            }
        }));
    }
    return result(AGENT_TOOL_IDS.overflowGuideline, facts, violations, {
        totalControlsCount,
        heatingZoneCount,
        fcuHeatingCoverage,
        heatingZonesBeyondPrimaryFcuCount,
        availableFcuSecondZoneCapacity,
        genuineOverflowBeforeExtra710,
        genuineOverflowAfterHeatOnly710,
        mzControllerCount,
        proposedMzTotalCapacity: mzZoneCapacity,
        proposedMzCapacityExcessZones: mzCapacityExcessAfterHeatOnly710,
        heatOnly710Count
    });
}
export function checkInfrastructureCapacity(input) {
    const capacity = summarizeInfrastructureCapacity(input.proposedItems);
    const ioComRelaySizingEvidence = input.required.ioComRelaySizingEvidence;
    const ioComRelaySizingItems = input.proposedItems.filter(isIoComRelaySizingItem);
    const ioComModuleCounts = countIoComRelayModules(ioComRelaySizingItems);
    const ioComSizingCapacity = summarizeInfrastructureCapacity(ioComRelaySizingItems);
    const srmRelaySizingEvidence = input.required.srmRelaySizingEvidence;
    const srmRelaySizingItems = input.proposedItems.filter(isSrmRelaySizingItem);
    const centralRelayCoverage = summarizeCentralRelayCoverage(input.proposedItems);
    const decisiveIoComRelayDemand = ioComRelaySizingEvidence?.groupedRelayOutputCount ?? 0;
    const hasDecisiveIoComRelayDemand = decisiveIoComRelayDemand > 0;
    const requiredCentralRelayOutputs = decisiveIoComRelayDemand > 0
        ? decisiveIoComRelayDemand
        : (input.required.centralRelayOutputs ?? 0);
    const decisiveIndividualRelayDemand = srmRelaySizingEvidence?.individualRelayOutputCount ?? 0;
    const hasDecisiveIndividualRelayDemand = decisiveIndividualRelayDemand > 0;
    const legacyIndividualRelayDemand = input.required.individualRelayOutputs ?? 0;
    const srmRelaySizingEvidenceMissing = legacyIndividualRelayDemand > 0 && !hasDecisiveIndividualRelayDemand;
    const requiredIndividualRelayOutputs = decisiveIndividualRelayDemand > 0
        ? decisiveIndividualRelayDemand
        : legacyIndividualRelayDemand;
    const excessSrmRelayOutputs = srmRelaySizingEvidence !== undefined &&
        capacity.srmRelayOutputs > decisiveIndividualRelayDemand
        ? capacity.srmRelayOutputs - decisiveIndividualRelayDemand
        : 0;
    const requiredNetmasters = input.required.netmasterCount ?? 0;
    const requiredProtocols = input.required.protocols ?? [];
    const missingProtocols = requiredProtocols.filter((protocol) => !protocolSetHas(capacity.protocols, protocol));
    const expectedIoComPacking = hasDecisiveIoComRelayDemand && ioComRelaySizingEvidence
        ? packIoComRelayModules(ioComRelaySizingEvidence.groupedRelayOutputCount)
        : undefined;
    const ioComCompositionMismatch = expectedIoComPacking !== undefined &&
        (ioComModuleCounts.ioCom8r !== expectedIoComPacking.expectedIoCom8rCount ||
            ioComModuleCounts.ioCom4r !== expectedIoComPacking.expectedIoCom4rCount);
    const ioComSizingEvidenceRefsMissing = ioComRelaySizingEvidence && hasDecisiveIoComRelayDemand
        ? ioComRelaySizingItems
            .filter((item) => !hasIoComRelaySizingEvidenceRefs(item, ioComRelaySizingEvidence))
            .map((item) => item.id)
            .sort()
        : [];
    const srmSizingEvidenceRefsMissing = srmRelaySizingEvidence && hasDecisiveIndividualRelayDemand
        ? srmRelaySizingItems
            .filter((item) => !hasSrmRelaySizingEvidenceRefs(item, srmRelaySizingEvidence))
            .map((item) => item.id)
            .sort()
        : [];
    const srmSizingEvidenceSourceRefsMissing = srmRelaySizingEvidence && hasDecisiveIndividualRelayDemand
        ? missingSrmRelaySizingEvidenceSources(srmRelaySizingItems, srmRelaySizingEvidence)
        : [];
    const srmSizingEvidenceRefsMissingValue = srmSizingEvidenceRefsMissing.length > 0
        ? srmSizingEvidenceRefsMissing
        : srmSizingEvidenceSourceRefsMissing.map((source) => `source:${source}`);
    const facts = [
        fact({
            kind: "infrastructure.io_com_relay_headroom",
            label: "IO/COM relay output headroom",
            value: centralRelayCoverage.relayOutputs - requiredCentralRelayOutputs,
            details: {
                requiredCentralRelayOutputs,
                proposedIoComRelayOutputs: centralRelayCoverage.relayOutputs,
                proposedIdentityIoComRelayOutputs: ioComSizingCapacity.ioComRelayOutputs,
                nonIoComRelayCapacityItemIds: centralRelayCoverage.nonIoComItemIds,
                nonIoComRelayOutputCapacity: centralRelayCoverage.nonIoComRelayOutputs
            }
        }),
        ...(ioComRelaySizingEvidence
            ? [
                fact({
                    kind: "infrastructure.io_com_relay_sizing_evidence",
                    label: "Decisive IO/COM relay sizing evidence",
                    value: ioComRelaySizingEvidence.groupedRelayOutputCount,
                    details: {
                        evidence: ioComRelaySizingEvidence,
                        proposed: ioComModuleCounts,
                        expected: {
                            ioCom8r: ioComRelaySizingEvidence.expectedIoCom8rCount,
                            ioCom4r: ioComRelaySizingEvidence.expectedIoCom4rCount
                        }
                    }
                })
            ]
            : []),
        ...(ioComRelaySizingEvidence
            ? [
                fact({
                    kind: "infrastructure.io_com_sizing_evidence_refs_missing",
                    label: "IO/COM relay sizing evidence refs missing",
                    value: ioComSizingEvidenceRefsMissing,
                    details: {
                        missingSizingEvidenceRefItemCount: ioComSizingEvidenceRefsMissing.length,
                        missingSizingEvidenceRefItemIds: ioComSizingEvidenceRefsMissing
                    }
                })
            ]
            : []),
        fact({
            kind: "infrastructure.srm_relay_headroom",
            label: "SRM relay output headroom",
            value: capacity.srmRelayOutputs - requiredIndividualRelayOutputs,
            details: {
                requiredIndividualRelayOutputs,
                proposedSrmRelayOutputs: capacity.srmRelayOutputs
            }
        }),
        ...(srmRelaySizingEvidence
            ? [
                fact({
                    kind: "infrastructure.srm_relay_sizing_evidence",
                    label: "Decisive SRM/SP relay sizing evidence",
                    value: srmRelaySizingEvidence.individualRelayOutputCount,
                    details: {
                        evidence: srmRelaySizingEvidence
                    }
                })
            ]
            : []),
        ...(srmRelaySizingEvidenceMissing
            ? [
                fact({
                    kind: "infrastructure.srm_sizing_evidence_missing",
                    label: "SRM/SP relay sizing evidence missing",
                    value: true,
                    details: {
                        requiredIndividualRelayOutputs,
                        legacyIndividualRelayOutputs: legacyIndividualRelayDemand,
                        decisiveIndividualRelayOutputs: decisiveIndividualRelayDemand,
                        hasSrmRelaySizingEvidence: srmRelaySizingEvidence !== undefined
                    }
                })
            ]
            : []),
        ...(srmRelaySizingEvidence
            ? [
                fact({
                    kind: "infrastructure.srm_sizing_evidence_refs_missing",
                    label: "SRM/SP relay sizing evidence refs missing",
                    value: srmSizingEvidenceRefsMissingValue,
                    details: {
                        missingSizingEvidenceRefItemCount: srmSizingEvidenceRefsMissing.length,
                        missingSizingEvidenceRefItemIds: srmSizingEvidenceRefsMissing,
                        missingSizingEvidenceSourceCount: srmSizingEvidenceSourceRefsMissing.length,
                        missingSizingEvidenceSourceIds: srmSizingEvidenceSourceRefsMissing
                    }
                })
            ]
            : []),
        fact({
            kind: "infrastructure.netmaster_headroom",
            label: "NetMaster count headroom",
            value: capacity.netmasterCount - requiredNetmasters,
            details: {
                requiredNetmasters,
                proposedNetmasters: capacity.netmasterCount
            }
        })
    ];
    const violations = [];
    if (ioComSizingEvidenceRefsMissing.length > 0 && ioComRelaySizingEvidence) {
        violations.push(violation({
            id: `${input.apartmentTypeId}-io-com-sizing-evidence-refs-missing`,
            code: "infrastructure.io_com_sizing_evidence_refs_missing",
            severity: "blocker",
            why: `${ioComSizingEvidenceRefsMissing.length} proposed IO/COM relay module row(s) omit the decisive sizing evidence refs.`,
            suggestedRecheckPromptFragment: "Keep every proposed IO/COM relay module tied to the decisive takeoff-derived sizing evidence with source/rule refs, or revise the module choice honestly.",
            details: {
                missingSizingEvidenceRefItemIds: ioComSizingEvidenceRefsMissing,
                acceptedSizingRuleTraceRefs: [
                    ...IO_COM_RELAY_SIZING_RULE_TRACE_REFS,
                    ...ioComRelaySizingEvidence.ruleTraceRefs
                ],
                evidence: ioComRelaySizingEvidence
            }
        }));
    }
    if (requiredCentralRelayOutputs > 0 &&
        centralRelayCoverage.relayOutputs < requiredCentralRelayOutputs) {
        violations.push(violation({
            id: `${input.apartmentTypeId}-io-com-relay-shortfall`,
            code: "infrastructure.io_com_relay_shortfall",
            severity: "blocker",
            why: `Central relay output demand is ${requiredCentralRelayOutputs}, but proposed relay-capable IO/COM coverage is ${centralRelayCoverage.relayOutputs}.`,
            suggestedRecheckPromptFragment: "Recheck IO/COM relay module count against the decisive takeoff-derived central load outputs before returning.",
            details: {
                requiredCentralRelayOutputs,
                proposedIoComRelayOutputs: centralRelayCoverage.relayOutputs,
                proposedIdentityIoComRelayOutputs: ioComSizingCapacity.ioComRelayOutputs,
                ...(ioComRelaySizingEvidence
                    ? {
                        evidence: ioComRelaySizingEvidence,
                        ruleTraceRefs: ioComRelaySizingEvidence.ruleTraceRefs,
                        sourceRefIds: ioComRelaySizingEvidence.sourceRefs.map((sourceRef) => sourceRef.sourceId)
                    }
                    : {})
            }
        }));
    }
    if (hasDecisiveIoComRelayDemand &&
        centralRelayCoverage.nonIoComItemIds.length > 0 &&
        ioComRelaySizingEvidence) {
        violations.push(violation({
            id: `${input.apartmentTypeId}-io-com-relay-surface-mismatch`,
            code: "infrastructure.io_com_relay_surface_mismatch",
            severity: "blocker",
            why: `${centralRelayCoverage.nonIoComItemIds.length} proposed relay-capacity row(s) claim central relay output capacity outside the IO/COM module surface.`,
            suggestedRecheckPromptFragment: "Represent central relay output capacity as IO/COM relay module rows with the decisive sizing evidence refs; do not hide relay capacity under a generic relay, panel, or unrelated surface.",
            details: {
                nonIoComRelayCapacityItemIds: centralRelayCoverage.nonIoComItemIds,
                nonIoComRelayOutputCapacity: centralRelayCoverage.nonIoComRelayOutputs,
                proposedIdentityIoComRelayOutputs: ioComSizingCapacity.ioComRelayOutputs,
                evidence: ioComRelaySizingEvidence
            }
        }));
    }
    if (ioComCompositionMismatch && expectedIoComPacking && ioComRelaySizingEvidence) {
        violations.push(violation({
            id: `${input.apartmentTypeId}-io-com-relay-sizing-evidence`,
            code: "infrastructure.io_com_sizing_contradicts_evidence",
            severity: "blocker",
            why: `IO/COM relay module selection proposed IO/COM-8R=${ioComModuleCounts.ioCom8r}, IO/COM-4R=${ioComModuleCounts.ioCom4r}, but decisive takeoff evidence for ${expectedIoComPacking.groupedRelayOutputCount} grouped relay output(s) expects IO/COM-8R=${expectedIoComPacking.expectedIoCom8rCount}, IO/COM-4R=${expectedIoComPacking.expectedIoCom4rCount}.`,
            suggestedRecheckPromptFragment: "Recheck IO/COM relay module sizing against the decisive takeoff-derived relay-output evidence; keep any deviation only with current tender evidence that changes the relay-output basis.",
            details: {
                proposed: ioComModuleCounts,
                expected: {
                    ioCom8r: expectedIoComPacking.expectedIoCom8rCount,
                    ioCom4r: expectedIoComPacking.expectedIoCom4rCount
                },
                evidence: ioComRelaySizingEvidence,
                ruleTraceRefs: ioComRelaySizingEvidence.ruleTraceRefs,
                sourceRefIds: ioComRelaySizingEvidence.sourceRefs.map((sourceRef) => sourceRef.sourceId)
            }
        }));
    }
    if (srmRelaySizingEvidenceMissing) {
        violations.push(violation({
            id: `${input.apartmentTypeId}-srm-sizing-evidence-missing`,
            code: "infrastructure.srm_sizing_evidence_missing",
            severity: "blocker",
            why: `Individual relay output demand is ${requiredIndividualRelayOutputs}, but no positive SRM/SP sizing evidence object was supplied.`,
            suggestedRecheckPromptFragment: "Derive positive SRM/SP relay sizing evidence from towel-rail, EUFH, source-enable, or remote high-load demand before relying on SRM/SP relay capacity.",
            details: {
                requiredIndividualRelayOutputs,
                legacyIndividualRelayOutputs: legacyIndividualRelayDemand,
                decisiveIndividualRelayOutputs: decisiveIndividualRelayDemand,
                hasSrmRelaySizingEvidence: srmRelaySizingEvidence !== undefined
            }
        }));
    }
    if (srmSizingEvidenceRefsMissingValue.length > 0 && srmRelaySizingEvidence) {
        violations.push(violation({
            id: `${input.apartmentTypeId}-srm-sizing-evidence-refs-missing`,
            code: "infrastructure.srm_sizing_evidence_refs_missing",
            severity: "blocker",
            why: srmSizingEvidenceRefsMissing.length > 0
                ? `${srmSizingEvidenceRefsMissing.length} proposed SRM/SP relay row(s) omit decisive individual load-relay sizing evidence refs.`
                : `${srmSizingEvidenceSourceRefsMissing.length} active individual load-relay demand source(s) lack decisive SRM/SP row refs.`,
            suggestedRecheckPromptFragment: "Keep every proposed SRM/SP relay row tied to the decisive towel-rail, EUFH, source-enable, or remote high-load output demand with source/rule refs, or revise the relay count honestly.",
            details: {
                missingSizingEvidenceRefItemIds: srmSizingEvidenceRefsMissing,
                missingSizingEvidenceSourceIds: srmSizingEvidenceSourceRefsMissing,
                acceptedSizingRuleTraceRefs: activeSrmRelayTraceRefsForEvidence(srmRelaySizingEvidence),
                acceptedSizingSourceRefIds: srmRelaySizingEvidence.sourceRefs.map((sourceRef) => sourceRef.sourceId.trim()),
                evidence: srmRelaySizingEvidence
            }
        }));
    }
    if (excessSrmRelayOutputs > 0 && srmRelaySizingEvidence) {
        violations.push(violation({
            id: `${input.apartmentTypeId}-srm-relay-quantity-contradicts-evidence`,
            code: "infrastructure.srm_relay_quantity_contradicts_evidence",
            severity: "blocker",
            why: `Proposed SRM relay capacity is ${capacity.srmRelayOutputs}, but decisive takeoff evidence justifies ${decisiveIndividualRelayDemand} individual relay output(s).`,
            suggestedRecheckPromptFragment: "Keep SRM/SP relay quantity equal to the decisive individual relay-output evidence unless current tender evidence explicitly changes the sizing basis.",
            details: {
                requiredIndividualRelayOutputs,
                decisiveIndividualRelayOutputs: decisiveIndividualRelayDemand,
                proposedSrmRelayOutputs: capacity.srmRelayOutputs,
                excessSrmRelayOutputs,
                evidence: srmRelaySizingEvidence,
                ruleTraceRefs: srmRelaySizingEvidence.ruleTraceRefs,
                sourceRefIds: srmRelaySizingEvidence.sourceRefs.map((sourceRef) => sourceRef.sourceId)
            }
        }));
    }
    if (capacity.srmRelayOutputs < requiredIndividualRelayOutputs) {
        violations.push(violation({
            id: `${input.apartmentTypeId}-srm-relay-shortfall`,
            code: "infrastructure.srm_relay_shortfall",
            severity: "blocker",
            why: `Individual relay output demand is ${requiredIndividualRelayOutputs}, but proposed SRM relay capacity is ${capacity.srmRelayOutputs}.`,
            suggestedRecheckPromptFragment: "Recheck SRM relay count for individual or remote high-load outputs.",
            details: {
                requiredIndividualRelayOutputs,
                proposedSrmRelayOutputs: capacity.srmRelayOutputs
            }
        }));
    }
    if (capacity.netmasterCount < requiredNetmasters) {
        violations.push(violation({
            id: `${input.apartmentTypeId}-netmaster-shortfall`,
            code: "infrastructure.netmaster_shortfall",
            severity: "blocker",
            why: `NetMaster demand is ${requiredNetmasters}, but proposal includes ${capacity.netmasterCount}.`,
            suggestedRecheckPromptFragment: "Recheck supervisory, BMS, app, metering, and scheduling requirements against NetMaster provision.",
            details: {
                requiredNetmasters,
                proposedNetmasters: capacity.netmasterCount
            }
        }));
    }
    if (missingProtocols.length > 0) {
        violations.push(violation({
            id: `${input.apartmentTypeId}-infrastructure-protocol-shortfall`,
            code: "infrastructure.protocol_shortfall",
            severity: "warning",
            why: `Proposal infrastructure does not expose required protocol(s): ${missingProtocols.join(", ")}.`,
            suggestedRecheckPromptFragment: "Recheck IO/COM, relay, SRM, and NetMaster protocol roles against the tender integration requirements.",
            details: { missingProtocols }
        }));
    }
    return result(AGENT_TOOL_IDS.infrastructureCapacity, facts, violations, {
        requiredCentralRelayOutputs,
        proposedIoComRelayOutputs: centralRelayCoverage.relayOutputs,
        proposedIdentityIoComRelayOutputs: ioComSizingCapacity.ioComRelayOutputs,
        nonIoComRelayCapacityItemCount: centralRelayCoverage.nonIoComItemIds.length,
        nonIoComRelayOutputCapacity: centralRelayCoverage.nonIoComRelayOutputs,
        ...(ioComRelaySizingEvidence
            ? {
                decisiveIoComRelayOutputs: ioComRelaySizingEvidence.groupedRelayOutputCount,
                expectedIoCom8r: ioComRelaySizingEvidence.expectedIoCom8rCount,
                expectedIoCom4r: ioComRelaySizingEvidence.expectedIoCom4rCount,
                proposedIoCom8r: ioComModuleCounts.ioCom8r,
                proposedIoCom4r: ioComModuleCounts.ioCom4r,
                ioComSizingEvidenceRefsMissing: ioComSizingEvidenceRefsMissing.length
            }
            : {}),
        requiredIndividualRelayOutputs,
        proposedSrmRelayOutputs: capacity.srmRelayOutputs,
        srmSizingEvidenceMissing: srmRelaySizingEvidenceMissing ? 1 : 0,
        ...(srmRelaySizingEvidence
            ? {
                decisiveIndividualRelayOutputs: srmRelaySizingEvidence.individualRelayOutputCount,
                srmSizingEvidenceRefsMissing: srmSizingEvidenceRefsMissingValue.length,
                excessSrmRelayOutputs
            }
            : {}),
        requiredNetmasters,
        proposedNetmasters: capacity.netmasterCount,
        missingProtocolCount: missingProtocols.length
    });
}
export function checkMeteringProtocolFit(input) {
    const requiredProtocols = input.tender.protocols ?? [];
    const proposedProtocols = new Set(input.proposedRows.flatMap((row) => (row.protocols ?? []).map((protocol) => normalizeToken(protocol))));
    const proposedTopologies = new Set(input.proposedRows
        .map((row) => row.topology)
        .filter((topology) => topology !== undefined)
        .map((topology) => normalizeToken(topology)));
    const missingProtocols = requiredProtocols.filter((protocol) => !protocolSetHas(proposedProtocols, protocol));
    const requiredTopology = input.tender.topology;
    const topologyFits = requiredTopology === undefined ||
        proposedTopologies.has(normalizeToken(requiredTopology));
    const bmsExposureCovered = input.tender.bmsExposureRequired !== true ||
        input.proposedRows.some((row) => rowMentions(row, ["bms", "bacnet"]));
    const residentDisplayCovered = input.tender.residentDisplayRequired !== true ||
        input.proposedRows.some((row) => rowMentions(row, ["resident", "display", "ccs", "rdu"]));
    const facts = [
        fact({
            kind: "metering.decoupled_from_controller_topology",
            label: "Metering/protocol check is decoupled from controller topology",
            value: true
        }),
        fact({
            kind: "metering.required_protocols",
            label: "Required metering protocols",
            value: requiredProtocols,
            details: {
                proposedProtocols: [...proposedProtocols].sort()
            }
        })
    ];
    const violations = [];
    let suppressedDerivativeFindingCount = 0;
    // F2 fix: count rows whose protocols include at least one assumed
    // (not tender/LLM-evidenced) capability, so an assumption is never
    // silently indistinguishable from evidence in the tool's audit summary.
    const assumedCapabilityRows = input.proposedRows.filter((row) => (row.assumedProtocols ?? []).length > 0).length;
    if (!input.tender.meteringRequired) {
        return result(AGENT_TOOL_IDS.meteringProtocolFit, facts, violations, {
            meteringRequired: 0,
            proposedRows: input.proposedRows.length,
            assumedCapabilityRows
        });
    }
    const noProposedRows = input.proposedRows.length === 0;
    if (noProposedRows) {
        violations.push(violation({
            id: `${input.apartmentTypeId}-metering-missing`,
            code: "metering.required_rows_missing",
            severity: "blocker",
            why: "Tender requires metering, but no proposed metering/protocol rows were supplied.",
            suggestedRecheckPromptFragment: "Recheck metering separately from controller topology and emit source-backed metering/protocol rows.",
            details: { meteringRequired: true }
        }));
    }
    if (missingProtocols.length > 0 && !noProposedRows) {
        violations.push(violation({
            id: `${input.apartmentTypeId}-metering-protocol-mismatch`,
            code: "metering.protocol_mismatch",
            severity: "blocker",
            why: `Proposed metering rows do not match required protocol(s): ${missingProtocols.join(", ")}.`,
            suggestedRecheckPromptFragment: "Recheck tender metering protocol evidence and revise the metering/protocol rows without changing controller topology.",
            details: { missingProtocols }
        }));
    }
    else if (missingProtocols.length > 0) {
        suppressedDerivativeFindingCount += 1;
    }
    if (!topologyFits && requiredTopology && !noProposedRows) {
        violations.push(violation({
            id: `${input.apartmentTypeId}-metering-topology-mismatch`,
            code: "metering.topology_mismatch",
            severity: "warning",
            why: `Tender metering topology is ${requiredTopology}, but proposed rows use ${[...proposedTopologies].sort().join(", ") || "no topology"}.`,
            suggestedRecheckPromptFragment: "Recheck the metering topology evidence in the tender and keep it separate from thermal controller allocation.",
            details: {
                requiredTopology,
                proposedTopologies: [...proposedTopologies].sort()
            }
        }));
    }
    else if (!topologyFits && requiredTopology) {
        suppressedDerivativeFindingCount += 1;
    }
    if (!bmsExposureCovered && !noProposedRows) {
        violations.push(violation({
            id: `${input.apartmentTypeId}-metering-bms-exposure-missing`,
            code: "metering.bms_exposure_missing",
            severity: "warning",
            why: "Tender requires metering exposure to BMS, but proposed metering rows do not mention BMS/BACnet exposure.",
            suggestedRecheckPromptFragment: "Recheck whether metering values need BACnet/IP or BMS exposure from the NetMaster or gateway.",
            details: { bmsExposureRequired: true }
        }));
    }
    else if (!bmsExposureCovered) {
        suppressedDerivativeFindingCount += 1;
    }
    if (!residentDisplayCovered && !noProposedRows) {
        violations.push(violation({
            id: `${input.apartmentTypeId}-metering-resident-display-missing`,
            code: "metering.resident_display_missing",
            severity: "warning",
            why: "Tender requires resident metering display, but proposed metering rows do not mention resident display, CCS, or RDU.",
            suggestedRecheckPromptFragment: "Recheck resident metering display requirements separately from meter bus topology.",
            details: { residentDisplayRequired: true }
        }));
    }
    else if (!residentDisplayCovered) {
        suppressedDerivativeFindingCount += 1;
    }
    return result(AGENT_TOOL_IDS.meteringProtocolFit, facts, violations, {
        meteringRequired: input.tender.meteringRequired ? 1 : 0,
        proposedRows: input.proposedRows.length,
        missingProtocolCount: missingProtocols.length,
        topologyMismatch: topologyFits ? 0 : 1,
        suppressedDerivativeFindingCount,
        assumedCapabilityRows
    }, suppressedDerivativeFindingCount > 0
        ? ["metering.empty_rows_derivatives_suppressed"]
        : []);
}
export function checkPolicyDefaultLabelling(input) {
    const unlabelledDefaults = input.decisions
        .filter((decision) => decision.defaulted)
        .filter((decision) => !hasCompleteDefaultLabel(decision))
        .map((decision) => decision.id)
        .sort();
    const missingMaterialFieldBasis = (input.materialFieldBasisEntries ?? [])
        .flatMap((entry) => materialFieldBasisGaps(entry))
        .sort();
    const installIncludedBasisContradictions = (input.materialFieldBasisEntries ?? [])
        .filter((entry) => entry.entryKind === "cabling_note" &&
        entry.installIncluded === true &&
        entry.installIncludedBasis === "policy_default")
        .map((entry) => `${entry.id}.installIncludedBasis`)
        .sort();
    const basisEvidenceUnresolved = (input.materialFieldBasisEntries ?? [])
        .flatMap((entry) => materialBasisEvidenceUnresolved(entry))
        .sort();
    const materialValueContradictions = (input.materialFieldBasisEntries ?? [])
        .flatMap((entry) => materialValueEvidenceContradictions(entry))
        .sort((left, right) => left.fieldRef.localeCompare(right.fieldRef));
    const materialValueConflicts = (input.materialFieldBasisEntries ?? [])
        .flatMap((entry) => materialValueEvidenceConflicts(entry))
        .sort((left, right) => left.fieldRef.localeCompare(right.fieldRef));
    const materialValueOverrides = (input.materialFieldBasisEntries ?? [])
        .flatMap((entry) => materialValueEvidenceOverrides(entry))
        .sort((left, right) => left.fieldRef.localeCompare(right.fieldRef));
    const materialPresenceDemandViolations = materialPresenceDemandFindings(input.materialPresenceDemands ?? [], input.materialFieldBasisEntries ?? []);
    const materialValueEvidenceMap = materialValueEvidenceMapForFacts(input.materialFieldBasisEntries ?? []);
    const facts = [
        fact({
            kind: "policy.defaults_unlabelled",
            label: "Unlabelled defaulted decisions",
            value: unlabelledDefaults,
            details: {
                defaultedDecisionCount: input.decisions.filter((decision) => decision.defaulted)
                    .length
            }
        }),
        fact({
            kind: "policy.material_field_basis_missing",
            label: "Material infrastructure fields missing basis disclosure",
            value: missingMaterialFieldBasis,
            details: {
                missingMaterialFieldBasisCount: missingMaterialFieldBasis.length
            }
        }),
        fact({
            kind: "policy.install_included_basis_contradictions",
            label: "Install-included values contradicted by their stated basis",
            value: installIncludedBasisContradictions,
            details: {
                installIncludedBasisContradictionCount: installIncludedBasisContradictions.length
            }
        }),
        fact({
            kind: "policy.basis_evidence_unresolved",
            label: "Evidence-backed material basis fields without resolved evidence",
            value: basisEvidenceUnresolved,
            details: {
                basisEvidenceUnresolvedCount: basisEvidenceUnresolved.length
            }
        }),
        fact({
            kind: "policy.material_value_evidence_map",
            label: "Evidence-supported material values",
            value: materialValueEvidenceMap,
            details: {
                materialValueEvidenceEntryCount: Object.keys(materialValueEvidenceMap).length
            }
        }),
        fact({
            kind: "policy.material_value_contradicts_evidence",
            label: "Material values contradicted by decisive evidence",
            value: materialValueContradictions.map((contradiction) => contradiction.fieldRef),
            details: {
                materialValueContradictionCount: materialValueContradictions.length,
                materialValueContradictions
            }
        }),
        fact({
            kind: "policy.material_value_evidence_conflicts",
            label: "Same-authority material evidence conflicts requiring user decision",
            value: materialValueConflicts.map((conflict) => conflict.fieldRef),
            details: {
                materialValueConflictCount: materialValueConflicts.length,
                materialValueConflicts
            }
        }),
        fact({
            kind: "policy.material_value_evidence_overrides",
            label: "Lower-authority material evidence overridden",
            value: materialValueOverrides.map((override) => override.fieldRef),
            details: {
                materialValueOverrideCount: materialValueOverrides.length,
                materialValueOverrides
            }
        }),
        fact({
            kind: "policy.material_presence_demands",
            label: "Decisive material presence demands",
            value: materialPresenceDemandViolations.demands.map((demand) => demand.id),
            details: {
                materialPresenceDemandCount: materialPresenceDemandViolations.demands.length,
                demands: materialPresenceDemandViolations.demands
            }
        }),
        fact({
            kind: "policy.material_presence_contradicts_evidence",
            label: "Material presence values contradicted by decisive evidence",
            value: materialPresenceDemandViolations.findings.map((finding) => finding.fieldRef),
            details: {
                materialPresenceContradictionCount: materialPresenceDemandViolations.findings.length,
                materialPresenceContradictions: materialPresenceDemandViolations.findings
            }
        })
    ];
    const violations = [];
    if (unlabelledDefaults.length > 0) {
        violations.push(violation({
            id: `${input.apartmentTypeId}-unlabelled-defaults`,
            code: "policy.default_unlabelled",
            severity: "warning",
            why: `${unlabelledDefaults.length} defaulted decision(s) lack WHY/WHERE or the explicit default-no-trigger label.`,
            suggestedRecheckPromptFragment: "For every defaulted decision, add WHY/WHERE evidence or label it exactly as default - no trigger found.",
            details: { unlabelledDefaults }
        }));
    }
    if (missingMaterialFieldBasis.length > 0) {
        violations.push(violation({
            id: `${input.apartmentTypeId}-material-field-basis-missing`,
            code: "policy.material_basis_missing",
            severity: "blocker",
            why: `${missingMaterialFieldBasis.length} material infrastructure field basis disclosure(s) are missing.`,
            suggestedRecheckPromptFragment: "For every infrastructure item, set provisionScopeBasis and installIncludedBasis honestly to tender_evidence, catalog_scope_evidence, or policy_default.",
            details: { missingMaterialFieldBasis }
        }));
    }
    if (installIncludedBasisContradictions.length > 0) {
        violations.push(violation({
            id: `${input.apartmentTypeId}-install-included-basis-contradiction`,
            code: "policy.install_included_basis_contradiction",
            severity: "blocker",
            why: `${installIncludedBasisContradictions.length} cabling-note installIncluded value(s) are true while the basis says policy_default.`,
            suggestedRecheckPromptFragment: "For cabling notes, use installIncluded=false when the basis is policy_default; set installIncluded=true only when tender_evidence or catalog_scope_evidence genuinely puts installation in scope.",
            details: { installIncludedBasisContradictions }
        }));
    }
    if (basisEvidenceUnresolved.length > 0) {
        violations.push(violation({
            id: `${input.apartmentTypeId}-basis-evidence-unresolved`,
            code: "policy.basis_evidence_unresolved",
            severity: "blocker",
            why: `${basisEvidenceUnresolved.length} evidence-backed material basis field(s) do not cite resolved evidence for the asserted value.`,
            suggestedRecheckPromptFragment: "For tender_evidence or catalog_scope_evidence basis fields, cite field-relevant sourceRefs/ruleTraceRefs that support the asserted value. Use catalog_scope_evidence, not tender_evidence, when the support is a catalog/ruleTraceRef such as a panel, screen, IO, or metering catalog rule; reserve tender_evidence for quote-bearing tender/source refs that speak to this field and value.",
            details: { basisEvidenceUnresolved }
        }));
    }
    if (materialValueContradictions.length > 0) {
        violations.push(violation({
            id: `${input.apartmentTypeId}-material-value-contradicts-evidence`,
            code: "policy.material_value_contradicts_evidence",
            severity: "blocker",
            why: `${materialValueContradictions.length} material field value(s) contradict the evidence-supported value map.`,
            suggestedRecheckPromptFragment: "Use the highest-authority resolved material evidence for each infrastructure field: tender_evidence outranks catalog_scope_evidence, which outranks policy_default. Change the proposed value or cite stronger resolved current evidence.",
            details: { materialValueContradictions }
        }));
    }
    if (materialValueConflicts.length > 0) {
        violations.push(violation({
            id: `${input.apartmentTypeId}-material-value-evidence-conflict`,
            code: "policy.material_value_evidence_conflict",
            severity: "blocker",
            why: `${materialValueConflicts.length} material field(s) have same-authority evidence supporting conflicting values and require a user decision.`,
            suggestedRecheckPromptFragment: "Do not silently pick one value when same-authority tender/catalog evidence conflicts. Surface the conflicting refs and ask for a user decision before emitting the material field.",
            details: { materialValueConflicts }
        }));
    }
    if (materialValueOverrides.length > 0) {
        violations.push(violation({
            id: `${input.apartmentTypeId}-material-value-evidence-overridden`,
            code: "policy.material_value_evidence_overridden",
            severity: "warning",
            why: `${materialValueOverrides.length} lower-authority material default(s) were overridden by stronger resolved evidence.`,
            suggestedRecheckPromptFragment: "Keep the stronger resolved tender/catalog evidence cited on the item, and leave the lower-authority policy default disclosed as overridden rather than treating it as decisive.",
            details: { materialValueOverrides }
        }));
    }
    if (materialPresenceDemandViolations.findings.length > 0) {
        violations.push(violation({
            id: `${input.apartmentTypeId}-material-presence-contradicts-evidence`,
            code: "policy.material_presence_contradicts_evidence",
            severity: "blocker",
            why: `${materialPresenceDemandViolations.findings.length} material presence or quantity field(s) contradict decisive mirror-demand evidence.`,
            suggestedRecheckPromptFragment: "Recheck decisive material presence demands. Emit required infrastructure mirrors at the demanded quantity and kind, and remove mirror rows when demand is zero unless stronger current evidence is cited.",
            details: {
                materialPresenceContradictions: materialPresenceDemandViolations.findings
            }
        }));
    }
    return result(AGENT_TOOL_IDS.policyDefaultLabelling, facts, violations, {
        defaultedDecisions: input.decisions.filter((decision) => decision.defaulted).length,
        unlabelledDefaults: unlabelledDefaults.length,
        missingMaterialFieldBasis: missingMaterialFieldBasis.length,
        installIncludedBasisContradictions: installIncludedBasisContradictions.length,
        basisEvidenceUnresolved: basisEvidenceUnresolved.length,
        materialValueContradictions: materialValueContradictions.length,
        materialValueConflicts: materialValueConflicts.length,
        materialValueOverrides: materialValueOverrides.length,
        materialPresenceContradictions: materialPresenceDemandViolations.findings.length
    });
}
export function checkProvenanceCompleteness(input) {
    const missingProvenanceEntryIds = input.entries
        .filter((entry) => (entry.sourceRefs ?? []).length === 0 && (entry.evidenceRefs ?? []).length === 0)
        .map((entry) => entry.id)
        .sort();
    const facts = [
        fact({
            kind: "provenance.entries_missing_source_or_evidence",
            label: "Entries missing source/evidence refs",
            value: missingProvenanceEntryIds,
            details: {
                checkedEntryCount: input.entries.length
            }
        })
    ];
    const violations = missingProvenanceEntryIds.length > 0
        ? [
            violation({
                id: `${input.apartmentTypeId}-missing-provenance`,
                code: "provenance.source_or_evidence_missing",
                severity: "warning",
                why: `${missingProvenanceEntryIds.length} proposal entries lack source or evidence refs.`,
                suggestedRecheckPromptFragment: "Recheck provenance before returning; every proposal entry needs sourceRefs or evidenceRefs.",
                details: { missingProvenanceEntryIds }
            })
        ]
        : [];
    return result(AGENT_TOOL_IDS.provenanceCompleteness, facts, violations, {
        checkedEntries: input.entries.length,
        missingProvenanceEntries: missingProvenanceEntryIds.length
    });
}
/**
 * Explicit controller decisions (previously accepted, pinned outside the
 * agent's own composition) and the agent's composed proposal can disagree.
 * This tool never resolves that disagreement — it only reports it as a fact
 * and, when material, a violation — so the agent must reconcile or explain
 * the change itself. It must run whenever an explicit decision exists,
 * independent of whether the proposal happens to cover the same zone set.
 */
export function checkControllerAllocationAgreement(input) {
    const explicitControllers = input.explicitControllers ?? [];
    const explicitProvided = explicitControllers.length > 0;
    if (!explicitProvided) {
        return result(AGENT_TOOL_IDS.controllerAllocationAgreement, [
            fact({
                kind: "controller.explicit_decision_not_provided",
                label: "No explicit controller decision supplied for agreement check",
                value: false
            })
        ], [], { explicitControllersProvided: 0 });
    }
    const proposalById = new Map(input.proposalControllers.map((controller) => [controller.id, controller]));
    const explicitById = new Map(explicitControllers.map((controller) => [controller.id, controller]));
    const missingFromProposal = explicitControllers
        .filter((controller) => !proposalById.has(controller.id))
        .map((controller) => controller.id)
        .sort();
    const extraInProposal = input.proposalControllers
        .filter((controller) => !explicitById.has(controller.id))
        .map((controller) => controller.id)
        .sort();
    const shapeMismatches = explicitControllers
        .filter((controller) => proposalById.has(controller.id))
        .filter((controller) => !controllerAllocationSignaturesMatch(controller, proposalById.get(controller.id)))
        .map((controller) => controller.id)
        .sort();
    const facts = [
        fact({
            kind: "controller.explicit_decision_provided",
            label: "Explicit controller decision supplied for agreement check",
            value: true,
            details: { explicitControllerCount: explicitControllers.length }
        }),
        fact({
            kind: "controller.explicit_missing_from_proposal",
            label: "Explicit controller ids absent from the composed proposal",
            value: missingFromProposal,
            details: { missingFromProposalCount: missingFromProposal.length }
        }),
        fact({
            kind: "controller.proposal_extra_controllers",
            label: "Composed proposal controller ids absent from the explicit decision",
            value: extraInProposal,
            details: { extraInProposalCount: extraInProposal.length }
        }),
        fact({
            kind: "controller.explicit_shape_mismatch",
            label: "Controller ids where the explicit decision and proposal disagree on shape",
            value: shapeMismatches,
            details: { shapeMismatchCount: shapeMismatches.length }
        })
    ];
    const disagreementCount = missingFromProposal.length + extraInProposal.length + shapeMismatches.length;
    const violations = disagreementCount > 0
        ? [
            violation({
                id: `${input.apartmentTypeId}-controller-explicit-decision-disagreement`,
                code: "controller.explicit_decision_disagreement",
                severity: "warning",
                why: `The composed proposal disagrees with the previously supplied explicit controller decision: ${missingFromProposal.length} missing, ${extraInProposal.length} extra, ${shapeMismatches.length} shape mismatch(es).`,
                suggestedRecheckPromptFragment: "Recheck the disagreement between your composed proposal and the previously accepted explicit controller decision before returning - reconcile intentionally and explain the change, do not let either side be silently overridden.",
                details: { missingFromProposal, extraInProposal, shapeMismatches }
            })
        ]
        : [];
    return result(AGENT_TOOL_IDS.controllerAllocationAgreement, facts, violations, {
        explicitControllersProvided: 1,
        missingFromProposal: missingFromProposal.length,
        extraInProposal: extraInProposal.length,
        shapeMismatches: shapeMismatches.length
    });
}
function controllerAllocationSignaturesMatch(left, right) {
    return (left.productPartNo === right.productPartNo &&
        (left.role ?? null) === (right.role ?? null) &&
        JSON.stringify(canonicalControllerAllocationZoneSlots(left.zoneSlots)) ===
            JSON.stringify(canonicalControllerAllocationZoneSlots(right.zoneSlots)));
}
function canonicalControllerAllocationZoneSlots(slots) {
    return [...slots]
        .map((slot) => ({
        slot: slot.slot,
        logicalZoneId: slot.logicalZoneId,
        role: slot.role,
        combinedLoadZoneIds: [...(slot.combinedLoadZoneIds ?? [])].sort()
    }))
        .sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right)));
}
function summarizeControllerCapacity(controller, apartmentPointDemands) {
    const ownedZoneIds = new Set(controller.zoneSlots.flatMap((slot) => [
        slot.logicalZoneId,
        ...(slot.combinedLoadZoneIds ?? [])
    ]));
    const demandPool = controller.pointUsage !== undefined
        ? []
        : (controller.pointDemands ?? apartmentPointDemands);
    const pointsUsed = controller.pointUsage ??
        sumPoints(demandPool
            .filter((demand) => ownedZoneIds.has(demand.ownerRef))
            .filter((demand) => demand.activeState === undefined || demand.activeState === "active")
            .filter((demand) => demand.consumesHardIo !== false)
            .map((demand) => demand.points));
    const zonesSupported = controller.supportedZoneCount ??
        defaultSupportedZoneCount(controller.productPartNo);
    return {
        controllerId: controller.id,
        productPartNo: controller.productPartNo,
        ...(controller.role ? { role: controller.role } : {}),
        pointsUsed,
        pointCapacity: controller.capacity,
        pointSpare: subtractNonNegative(controller.capacity, pointsUsed),
        zonesAssigned: controller.zoneSlots.length,
        zonesSupported,
        hasSecondSlot: controller.zoneSlots.some((slot) => slot.slot === 2)
    };
}
function hasSpareSecondZoneSlot(summary, secondaryZoneDemand) {
    return (summary.productPartNo === "TP-C-710R-IP" &&
        summary.role === "fcu_primary" &&
        !summary.hasSecondSlot &&
        summary.zonesAssigned < summary.zonesSupported &&
        POINT_KINDS.every((pointKind) => summary.pointSpare[pointKind] >= secondaryZoneDemand[pointKind]));
}
function collectAssignedZoneIds(controllers) {
    const assignedZoneIds = new Set();
    for (const controller of controllers) {
        for (const slot of controller.zoneSlots) {
            assignedZoneIds.add(slot.logicalZoneId);
            for (const combinedZoneId of slot.combinedLoadZoneIds ?? []) {
                assignedZoneIds.add(combinedZoneId);
            }
        }
    }
    return assignedZoneIds;
}
function isSecondZoneAssociationCandidate(zone) {
    return (zone.zoneKind === "wufh" ||
        zone.zoneKind === "eufh" ||
        zone.zoneKind === "heat_only");
}
function summarizeRoomCoverage(input, zoneCoverage) {
    const rooms = input.rooms ?? [];
    if (rooms.length === 0) {
        return [];
    }
    const uncoveredRooms = rooms
        .filter((room) => {
        const zoneIds = room.zoneIds ??
            input.zones.filter((zone) => zone.roomId === room.id).map((zone) => zone.id);
        return zoneIds.every((zoneId) => (zoneCoverage.get(zoneId) ?? []).length === 0);
    })
        .map((room) => room.id)
        .sort();
    return [
        fact({
            kind: "coverage.uncovered_rooms",
            label: "Rooms with no covered zones",
            value: uncoveredRooms,
            details: { uncoveredRoomCount: uncoveredRooms.length }
        })
    ];
}
function normalizeOverflowTakeoff(takeoff) {
    return {
        apartmentTypeId: takeoff.apartmentTypeId,
        fcuCount: takeoff.fcuCount ?? 0,
        wufhZoneCount: takeoff.wufhZoneCount ?? 0,
        eufhZoneCount: takeoff.eufhZoneCount ?? 0,
        standaloneHeatOnlyZoneCount: takeoff.standaloneHeatOnlyZoneCount ?? 0
    };
}
function summarizeInfrastructureCapacity(proposedItems) {
    let ioComRelayOutputs = 0;
    let srmRelayOutputs = 0;
    let netmasterCount = 0;
    const protocols = new Set();
    for (const item of proposedItems) {
        const quantity = Math.max(0, item.quantity);
        const productPartNo = item.productPartNo ?? "";
        const relayCapacity = item.relayOutputCapacity ?? defaultRelayOutputCapacity(productPartNo);
        if (isIoComRelayModule(item, productPartNo)) {
            ioComRelayOutputs += quantity * relayCapacity;
        }
        if (isSrmRelay(item, productPartNo)) {
            srmRelayOutputs += quantity * (relayCapacity || 1);
        }
        if (item.itemKind === "netmaster" || productPartNo.startsWith("TP-NM-")) {
            netmasterCount += quantity;
        }
        for (const protocol of [
            ...(item.protocols ?? []),
            ...(item.metering?.protocols ?? [])
        ]) {
            protocols.add(normalizeToken(protocol));
        }
    }
    return {
        ioComRelayOutputs,
        srmRelayOutputs,
        netmasterCount,
        protocols
    };
}
function summarizeCentralRelayCoverage(proposedItems) {
    let relayOutputs = 0;
    let nonIoComRelayOutputs = 0;
    const nonIoComItemIds = [];
    for (const item of proposedItems) {
        const quantity = Math.max(0, item.quantity);
        const productPartNo = item.productPartNo ?? "";
        const isIoCom = isIoComRelayModule(item, productPartNo.toUpperCase());
        const relayCapacity = isIoCom
            ? (item.relayOutputCapacity ?? defaultRelayOutputCapacity(productPartNo))
            : (item.relayOutputCapacity ?? 0);
        if (relayCapacity <= 0) {
            continue;
        }
        const relayOutputCapacity = quantity * relayCapacity;
        relayOutputs += relayOutputCapacity;
        if (!isIoCom) {
            nonIoComRelayOutputs += relayOutputCapacity;
            nonIoComItemIds.push(item.id);
        }
    }
    return {
        relayOutputs,
        nonIoComRelayOutputs,
        nonIoComItemIds: nonIoComItemIds.sort()
    };
}
function countIoComRelayModules(proposedItems) {
    let ioCom8r = 0;
    let ioCom4r = 0;
    for (const item of proposedItems) {
        const productPartNo = (item.productPartNo ?? "").toUpperCase();
        if (!isIoComRelayModule(item, productPartNo)) {
            continue;
        }
        const descriptor = `${productPartNo} ${(item.productFamily ?? "").toUpperCase()}`;
        if (descriptor.includes("IO/COM-8R") || descriptor.includes("IO-COM-8R")) {
            ioCom8r += item.quantity;
        }
        if (descriptor.includes("IO/COM-4R") || descriptor.includes("IO-COM-4R")) {
            ioCom4r += item.quantity;
        }
    }
    return { ioCom8r, ioCom4r };
}
function isIoComRelaySizingItem(item) {
    const productPartNo = (item.productPartNo ?? "").toUpperCase();
    return isIoComRelayModule(item, productPartNo);
}
function isSrmRelaySizingItem(item) {
    const productPartNo = (item.productPartNo ?? "").toUpperCase();
    return isSrmRelay(item, productPartNo);
}
function hasIoComRelaySizingEvidenceRefs(item, evidence) {
    const traceRefs = new Set([...(item.ruleTraceRefs ?? []), ...(item.evidenceRefs ?? [])].map((ref) => normalizeEvidenceRef(ref)));
    const acceptedRefs = [
        ...IO_COM_RELAY_SIZING_RULE_TRACE_REFS,
        ...evidence.ruleTraceRefs
    ].map((ref) => normalizeEvidenceRef(ref));
    return acceptedRefs.some((ref) => traceRefs.has(ref));
}
function hasSrmRelaySizingEvidenceRefs(item, evidence) {
    const traceRefs = normalizedItemTraceRefs(item);
    const activeSources = activeSrmRelayOutputSourcesForEvidence(evidence);
    const activeCoverage = srmRelaySourceCoverageForItem(item, evidence);
    const hasActiveDemandTraceRef = activeCoverage.size > 0;
    if (hasActiveDemandTraceRef) {
        return true;
    }
    const activeTraceRefs = new Set(activeSrmRelayTraceRefsForEvidence(evidence).map((ref) => normalizeEvidenceRef(ref)));
    const hasInactiveSourceSpecificTraceRef = [...SRM_RELAY_SOURCE_SPECIFIC_TRACE_REFS]
        .filter((ref) => !activeTraceRefs.has(ref))
        .some((ref) => traceRefs.has(ref));
    if (hasInactiveSourceSpecificTraceRef) {
        return false;
    }
    const itemSourceIds = new Set((item.sourceRefs ?? [])
        .map((sourceRef) => normalizeEvidenceRef(sourceRef.sourceId))
        .filter((sourceId) => sourceId.length > 0));
    const acceptedSourceIds = evidence.sourceRefs
        .map((sourceRef) => normalizeEvidenceRef(sourceRef.sourceId))
        .filter((sourceId) => sourceId.length > 0);
    return (activeSources.length === 1 &&
        acceptedSourceIds.some((sourceId) => itemSourceIds.has(sourceId) || traceRefs.has(sourceId)));
}
function missingSrmRelaySizingEvidenceSources(items, evidence) {
    const activeSources = activeSrmRelayOutputSourcesForEvidence(evidence);
    if (items.length === 0 || activeSources.length <= 1) {
        return [];
    }
    const coveredSources = new Set(items.flatMap((item) => [...srmRelaySourceCoverageForItem(item, evidence)]));
    return activeSources.filter((source) => !coveredSources.has(source));
}
function srmRelaySourceCoverageForItem(item, evidence) {
    const traceRefs = normalizedItemTraceRefs(item);
    const coveredSources = new Set();
    for (const source of activeSrmRelayOutputSourcesForEvidence(evidence)) {
        const sourceTraceRefs = SRM_RELAY_SOURCE_TRACE_REFS_BY_SOURCE[source] ?? [];
        if (sourceTraceRefs.some((ref) => traceRefs.has(normalizeEvidenceRef(ref)))) {
            coveredSources.add(source);
        }
    }
    return coveredSources;
}
function normalizedItemTraceRefs(item) {
    return new Set([...(item.ruleTraceRefs ?? []), ...(item.evidenceRefs ?? [])]
        .map((ref) => normalizeEvidenceRef(ref))
        .filter((ref) => ref.length > 0));
}
function isIoComRelayModule(item, productPartNo) {
    const productFamily = (item.productFamily ?? "").toUpperCase();
    return (item.itemKind === "io_module" ||
        productPartNo.startsWith("IO/COM-") ||
        productPartNo.startsWith("IO-COM-") ||
        productFamily.includes("IO/COM") ||
        productFamily.includes("IO-COM"));
}
function isSrmRelay(item, productPartNo) {
    return (item.itemKind === "relay" &&
        normalizeSrmSpRelayPartNo(productPartNo) === SRM_SP_RELAY_CANONICAL_PART_NO);
}
function defaultRelayOutputCapacity(productPartNo) {
    if (productPartNo.includes("8R")) {
        return 8;
    }
    if (productPartNo.includes("4R")) {
        return 4;
    }
    if (normalizeSrmSpRelayPartNo(productPartNo) === SRM_SP_RELAY_CANONICAL_PART_NO) {
        return 1;
    }
    return 0;
}
function normalizeSrmRelayOutputSource(source) {
    return normalizeToken(source);
}
function normalizeEvidenceRef(ref) {
    return ref.trim().toLowerCase();
}
function activeSrmRelayOutputSourcesForEvidence(evidence) {
    return [
        ...new Set(evidence.individualRelayOutputSources
            .map((source) => normalizeSrmRelayOutputSource(source))
            .filter((source) => source.length > 0))
    ];
}
function activeSrmRelayTraceRefsForEvidence(evidence) {
    return [
        ...new Set(activeSrmRelayOutputSourcesForEvidence(evidence).flatMap((source) => SRM_RELAY_SOURCE_TRACE_REFS_BY_SOURCE[source] ?? []))
    ];
}
function normalizeSrmSpRelayPartNo(productPartNo) {
    const normalizedPartNo = productPartNo.trim().toUpperCase();
    if (!SRM_SP_RELAY_ACCEPTED_PART_NOS.has(normalizedPartNo)) {
        return undefined;
    }
    return SRM_SP_RELAY_CANONICAL_PART_NO;
}
function defaultSupportedZoneCount(productPartNo) {
    if (productPartNo === "TP-C-710R-IP") {
        return 2;
    }
    if (productPartNo === "MZ-503") {
        return 4;
    }
    return 1;
}
function rowMentions(row, needles) {
    const haystack = [
        row.id,
        row.role,
        row.topology ?? "",
        ...(row.protocols ?? []),
        ...(row.notes ?? [])
    ]
        .join(" ")
        .toLowerCase();
    return needles.some((needle) => haystack.includes(needle));
}
function hasCompleteDefaultLabel(decision) {
    const label = normalizeDefaultLabel(decision.label ?? "");
    const hasExplicitDefaultLabel = label === "default no trigger found";
    const hasWhyWhere = (decision.why ?? "").trim().length > 0 && (decision.where ?? "").trim().length > 0;
    return hasExplicitDefaultLabel || hasWhyWhere;
}
function materialPresenceDemandFindings(demands, entries) {
    const findings = [];
    for (const demand of demands) {
        const matchingEntries = entries.filter((entry) => normalizeMaterialPartNo(entry.productPartNo) ===
            normalizeMaterialPartNo(demand.productPartNo));
        const actualQuantity = matchingEntries.reduce((sum, entry) => sum + (entry.quantity ?? 0), 0);
        const actualEntryKinds = sortedUniqueStrings(matchingEntries.map((entry) => entry.entryKind));
        const fieldRef = `${demand.id}.itemPresence`;
        if (actualQuantity !== demand.quantity) {
            findings.push({
                fieldRef,
                productPartNo: demand.productPartNo,
                expectedQuantity: demand.quantity,
                actualQuantity,
                expectedEntryKind: demand.entryKind,
                actualEntryKinds,
                evidenceRefs: demand.evidenceRefs,
                ...(demand.sourceRefs ? { sourceRefs: demand.sourceRefs } : {}),
                derivedFrom: demand.derivedFrom,
                why: `${demand.why} Proposed infrastructure quantity for ${demand.productPartNo} is ${actualQuantity}.`
            });
            continue;
        }
        if (demand.quantity > 0 &&
            !matchingEntries.every((entry) => entry.entryKind === demand.entryKind)) {
            findings.push({
                fieldRef: `${demand.id}.entryKind`,
                productPartNo: demand.productPartNo,
                expectedQuantity: demand.quantity,
                actualQuantity,
                expectedEntryKind: demand.entryKind,
                actualEntryKinds,
                evidenceRefs: demand.evidenceRefs,
                ...(demand.sourceRefs ? { sourceRefs: demand.sourceRefs } : {}),
                derivedFrom: demand.derivedFrom,
                why: `${demand.why} Proposed infrastructure kind(s) for ${demand.productPartNo} are ${actualEntryKinds.join(", ") || "none"}, expected ${demand.entryKind}.`
            });
        }
    }
    return {
        demands: [...demands].sort((left, right) => left.id.localeCompare(right.id)),
        findings: findings.sort((left, right) => left.fieldRef.localeCompare(right.fieldRef))
    };
}
function normalizeMaterialPartNo(value) {
    const normalized = value?.trim().toUpperCase();
    return normalized && normalized.length > 0 ? normalized : undefined;
}
function sortedUniqueStrings(values) {
    return [...new Set(values)].sort((left, right) => left.localeCompare(right));
}
function materialFieldBasisGaps(entry) {
    const gaps = [];
    if (entry.provisionScope !== undefined && entry.provisionScopeBasis === undefined) {
        gaps.push(`${entry.id}.provisionScopeBasis`);
    }
    if (entry.entryKind === "cabling_note" && entry.installIncluded === undefined) {
        gaps.push(`${entry.id}.installIncluded`);
    }
    if (entry.installIncluded !== undefined && entry.installIncludedBasis === undefined) {
        gaps.push(`${entry.id}.installIncludedBasis`);
    }
    return gaps;
}
function materialBasisEvidenceUnresolved(entry) {
    const unresolved = [];
    if (entry.provisionScope !== undefined &&
        isEvidenceBackedMaterialBasis(entry.provisionScopeBasis) &&
        !hasResolvedMaterialBasisEvidence(entry, "provisionScope", entry.provisionScope, entry.provisionScopeBasis)) {
        unresolved.push(`${entry.id}.provisionScopeBasis`);
    }
    if (entry.installIncluded !== undefined &&
        isEvidenceBackedMaterialBasis(entry.installIncludedBasis) &&
        !hasResolvedMaterialBasisEvidence(entry, "installIncluded", entry.installIncluded, entry.installIncludedBasis)) {
        unresolved.push(`${entry.id}.installIncludedBasis`);
    }
    return unresolved;
}
function materialValueEvidenceMapForFacts(entries) {
    const mappedEntries = entries
        .filter((entry) => (entry.supportedValueEvidence ?? []).length > 0)
        .map((entry) => [
        entry.id,
        (entry.supportedValueEvidence ?? []).map((support) => ({
            field: support.field,
            supportedValue: support.supportedValue,
            authority: materialSupportAuthority(support),
            evidenceKind: support.evidenceKind,
            evidenceRefs: support.evidenceRefs,
            ...(support.sourceRefs ? { sourceRefs: support.sourceRefs } : {}),
            ...(support.silence ? { silence: support.silence } : {}),
            why: support.why
        }))
    ]);
    return Object.fromEntries(mappedEntries.sort(([left], [right]) => left.localeCompare(right)));
}
function materialValueEvidenceContradictions(entry) {
    return materialValueEvidenceFindings(entry).contradictions;
}
function materialValueEvidenceOverrides(entry) {
    return materialValueEvidenceFindings(entry).overrides;
}
function materialValueEvidenceConflicts(entry) {
    return materialValueEvidenceFindings(entry).conflicts;
}
function materialValueEvidenceFindings(entry) {
    const contradictions = [];
    const overrides = [];
    const conflicts = [];
    const supportsByField = new Map();
    for (const support of materialValueSupportsForEntry(entry)) {
        supportsByField.set(support.field, [
            ...(supportsByField.get(support.field) ?? []),
            support
        ]);
    }
    for (const [field, supports] of supportsByField) {
        const actualValue = actualMaterialValue(entry, field);
        if (actualValue === null || supports.length === 0) {
            continue;
        }
        const highestAuthorityRank = Math.max(...supports.map((support) => materialSupportAuthorityRank(support)));
        const decisiveSupports = supports.filter((support) => materialSupportAuthorityRank(support) === highestAuthorityRank);
        const decisiveSupportedValues = uniqueMaterialSupportValues(decisiveSupports.map((support) => support.supportedValue));
        if (decisiveSupportedValues.length > 1) {
            conflicts.push({
                fieldRef: `${entry.id}.${field}`,
                actualValue,
                authority: materialSupportAuthority(decisiveSupports[0]),
                conflictingSupportedValues: decisiveSupportedValues,
                conflictingSupports: decisiveSupports.map((support) => ({
                    supportedValue: support.supportedValue,
                    evidenceKind: support.evidenceKind,
                    evidenceRefs: support.evidenceRefs,
                    ...(support.sourceRefs ? { sourceRefs: support.sourceRefs } : {}),
                    why: support.why
                })),
                why: `${entry.id}.${field} has same-authority evidence for ${decisiveSupportedValues
                    .map(String)
                    .join(" and ")}; a user decision is required.`
            });
            continue;
        }
        const matchingDecisiveSupport = decisiveSupports.find((support) => support.supportedValue === actualValue);
        if (!matchingDecisiveSupport) {
            contradictions.push(...decisiveSupports
                .filter((support) => support.supportedValue !== actualValue)
                .map((support) => ({
                fieldRef: `${entry.id}.${field}`,
                actualValue,
                supportedValue: support.supportedValue,
                evidenceKind: support.evidenceKind,
                authority: materialSupportAuthority(support),
                evidenceRefs: support.evidenceRefs,
                ...(support.sourceRefs ? { sourceRefs: support.sourceRefs } : {}),
                why: support.why
            })));
            continue;
        }
        overrides.push(...supports
            .filter((support) => materialSupportAuthorityRank(support) < highestAuthorityRank &&
            support.supportedValue !== actualValue)
            .map((support) => ({
            fieldRef: `${entry.id}.${field}`,
            actualValue,
            overriddenSupportedValue: support.supportedValue,
            overriddenAuthority: materialSupportAuthority(support),
            overriddenEvidenceKind: support.evidenceKind,
            overriddenEvidenceRefs: support.evidenceRefs,
            overriddenWhy: support.why,
            decisiveSupportedValue: matchingDecisiveSupport.supportedValue,
            decisiveAuthority: materialSupportAuthority(matchingDecisiveSupport),
            decisiveEvidenceKind: matchingDecisiveSupport.evidenceKind,
            decisiveEvidenceRefs: matchingDecisiveSupport.evidenceRefs,
            decisiveWhy: matchingDecisiveSupport.why
        })));
    }
    return { contradictions, overrides, conflicts };
}
function materialValueSupportsForEntry(entry) {
    return [
        ...(entry.supportedValueEvidence ?? []),
        ...resolvedBasisMaterialSupports(entry)
    ];
}
function resolvedBasisMaterialSupports(entry) {
    const supports = [];
    if (entry.provisionScope !== undefined &&
        isEvidenceBackedMaterialBasis(entry.provisionScopeBasis) &&
        hasResolvedMaterialBasisEvidence(entry, "provisionScope", entry.provisionScope, entry.provisionScopeBasis)) {
        supports.push(materialBasisSupport(entry, "provisionScope", entry.provisionScope), materialBasisSupport(entry, "provisionScopeBasis", entry.provisionScopeBasis));
    }
    if (entry.installIncluded !== undefined &&
        isEvidenceBackedMaterialBasis(entry.installIncludedBasis) &&
        hasResolvedMaterialBasisEvidence(entry, "installIncluded", entry.installIncluded, entry.installIncludedBasis)) {
        supports.push(materialBasisSupport(entry, "installIncluded", entry.installIncluded), materialBasisSupport(entry, "installIncludedBasis", entry.installIncludedBasis));
    }
    return supports;
}
function materialBasisSupport(entry, field, supportedValue) {
    const authority = field === "provisionScope" || field === "provisionScopeBasis"
        ? entry.provisionScopeBasis
        : entry.installIncludedBasis;
    if (!isEvidenceBackedMaterialBasis(authority)) {
        throw new Error(`Cannot build material basis support for ${entry.id}.${field}`);
    }
    return {
        field,
        supportedValue,
        authority,
        evidenceKind: authority,
        evidenceRefs: entry.evidenceRefs ?? [],
        sourceRefs: entry.sourceRefs ?? [],
        why: `${entry.id}.${field} cites resolved ${authority} for the asserted value.`
    };
}
function materialSupportAuthority(support) {
    return support.authority;
}
function materialSupportAuthorityRank(support) {
    switch (materialSupportAuthority(support)) {
        case "tender_evidence":
            return 3;
        case "catalog_scope_evidence":
            return 2;
        case "policy_default":
            return 1;
    }
}
function actualMaterialValue(entry, field) {
    switch (field) {
        case "itemPresence":
            return true;
        case "entryKind":
            return entry.entryKind;
        case "provisionScope":
            return entry.provisionScope ?? null;
        case "provisionScopeBasis":
            return entry.provisionScopeBasis ?? null;
        case "installIncluded":
            return entry.installIncluded ?? null;
        case "installIncludedBasis":
            return entry.installIncludedBasis ?? null;
    }
}
function isEvidenceBackedMaterialBasis(basis) {
    return basis !== undefined && EVIDENCE_BACKED_MATERIAL_BASIS.has(basis);
}
function hasResolvedMaterialBasisEvidence(entry, field, supportedValue, authority) {
    return ((entry.sourceRefs ?? []).some((sourceRef) => isResolvedFieldRelevantSourceRef(sourceRef, entry, field, supportedValue)) ||
        (entry.evidenceRefs ?? []).some((evidenceRef) => evidenceRefResolvesForMaterialClaim(evidenceRef, entry, field, supportedValue, authority)));
}
function isResolvedSourceRef(sourceRef) {
    return (sourceRef.sourceId.trim().length > 0 &&
        sourceRef.sourceKind.trim().length > 0 &&
        sourceRef.authorityClass.trim().length > 0 &&
        sourceRef.locator.trim().length > 0);
}
function isResolvedFieldRelevantSourceRef(sourceRef, entry, field, supportedValue) {
    if (!isResolvedSourceRef(sourceRef)) {
        return false;
    }
    const quote = normalizeEvidenceText(sourceRef.quote ?? "");
    return (quote.length > 0 &&
        includesAnyEvidenceNeedle(quote, materialSubjectNeedles(entry)) &&
        includesAnyEvidenceNeedle(quote, materialFieldNeedles(field)) &&
        includesAnyEvidenceNeedle(quote, materialValueNeedles(field, supportedValue)));
}
function evidenceRefResolvesForMaterialClaim(evidenceRef, entry, field, supportedValue, authority) {
    const normalizedEvidenceRef = evidenceRef.trim();
    if (normalizedEvidenceRef.length === 0) {
        return false;
    }
    return (entry.supportedValueEvidence ?? []).some((support) => support.field === field &&
        support.supportedValue === supportedValue &&
        materialSupportAuthority(support) === authority &&
        support.evidenceRefs.includes(normalizedEvidenceRef));
}
function uniqueMaterialSupportValues(values) {
    return values.reduce((uniqueValues, value) => {
        if (!uniqueValues.some((candidate) => candidate === value)) {
            uniqueValues.push(value);
        }
        return uniqueValues;
    }, []);
}
function materialSubjectNeedles(entry) {
    const rawNeedles = [entry.id, entry.entryKind, entry.entryKind.replace(/_/g, " ")];
    const normalizedRawNeedles = rawNeedles
        .map(normalizeEvidenceText)
        .filter((needle) => needle.length >= 3 &&
        !needle.split(" ").every((token) => GENERIC_MATERIAL_SUBJECT_TOKENS.has(token)));
    const tokens = rawNeedles.flatMap((value) => normalizeEvidenceText(value)
        .split(" ")
        .filter((token) => token.length >= 3 && !GENERIC_MATERIAL_SUBJECT_TOKENS.has(token)));
    return uniqueEvidenceNeedles([...normalizedRawNeedles, ...tokens]);
}
function materialFieldNeedles(field) {
    switch (field) {
        case "itemPresence":
        case "entryKind":
            return [
                "separate",
                "standalone",
                "scope",
                "line",
                "row",
                "item",
                "included",
                "excluded",
                "by others"
            ];
        case "provisionScope":
        case "provisionScopeBasis":
            return [
                "scope",
                "provision",
                "install",
                "installed",
                "installation",
                "included",
                "excluded",
                "by others",
                "project level",
                "per project",
                "shared infrastructure",
                "apartment type"
            ];
        case "installIncluded":
        case "installIncludedBasis":
            return [
                "install",
                "installed",
                "installation",
                "fitted",
                "fitting",
                "included",
                "excluded",
                "by others",
                "contractor"
            ];
    }
}
function materialValueNeedles(field, supportedValue) {
    if (field === "installIncluded") {
        return supportedValue === true
            ? ["install", "installed", "installation", "fitted", "included", "by titan"]
            : [
                "not installed",
                "not be installed",
                "excluded",
                "not included",
                "by others",
                "client supplied",
                "supplied by others"
            ];
    }
    if (field === "provisionScope") {
        return supportedValue === "project"
            ? ["project", "project level", "per project", "shared", "central"]
            : [
                "apartment",
                "apartment type",
                "per apartment",
                "each apartment",
                "dwelling",
                "flat"
            ];
    }
    return [String(supportedValue)];
}
function includesAnyEvidenceNeedle(text, needles) {
    return needles.length > 0 && needles.some((needle) => text.includes(needle));
}
function uniqueEvidenceNeedles(values) {
    return Array.from(new Set(values.filter((value) => value.trim().length > 0)));
}
function normalizeEvidenceText(value) {
    return value
        .toLowerCase()
        .replace(/[-_/]+/g, " ")
        .replace(/[^a-z0-9]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}
function normalizeDefaultLabel(value) {
    return value
        .toLowerCase()
        .replace(/[-\u2013\u2014]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}
function sumPoints(points) {
    return points.reduce((total, point) => ({
        ui: total.ui + point.ui,
        di: total.di + point.di,
        ao: total.ao + point.ao,
        do: total.do + point.do,
        relay: total.relay + point.relay
    }), ZERO_POINTS);
}
function subtractNonNegative(left, right) {
    return {
        ui: Math.max(0, left.ui - right.ui),
        di: Math.max(0, left.di - right.di),
        ao: Math.max(0, left.ao - right.ao),
        do: Math.max(0, left.do - right.do),
        relay: Math.max(0, left.relay - right.relay)
    };
}
function normalizeToken(value) {
    return value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
}
function protocolSetHas(protocols, required) {
    const normalizedRequired = normalizeToken(required);
    return [...protocols].some((protocol) => protocol === normalizedRequired ||
        protocol.startsWith(`${normalizedRequired}_`) ||
        protocol.endsWith(`_${normalizedRequired}`) ||
        protocol.includes(`_${normalizedRequired}_`));
}
function fact(input) {
    return input;
}
function violation(input) {
    return input;
}
function result(toolId, facts, violations, counts, extraFlags = []) {
    const hasHardViolation = violations.some((item) => item.severity === "error" || item.severity === "blocker");
    const status = violations.length === 0 ? "ok" : hasHardViolation ? "violation" : "advisory";
    return {
        contractVersion: AGENT_TOOL_CONTRACT_VERSION,
        toolId,
        convergenceDefaults: AGENT_TOOL_CONVERGENCE_DEFAULTS,
        facts,
        violations,
        machineSummary: {
            status,
            counts,
            flags: [...violations.map((item) => item.code), ...extraFlags],
            subjectRefs: [
                ...new Set(violations
                    .map((item) => item.subjectRef)
                    .filter((subjectRef) => subjectRef !== undefined))
            ].sort()
        }
    };
}
//# sourceMappingURL=index.js.map